// Карточка предпросмотра одного черновика рейса — все поля редактируемы до сохранения.
import type { ReactNode } from 'react';
import type { Currency, PriceScope } from '../../types';
import { KNOWN_TIMEZONES } from '../../parser';
import { useStore } from '../../store';
import { useT } from '../../i18n';
import type { DraftForm, DraftFormErrors } from './draftForm';
import styles from './SourceModal.module.css';

/** Подписи берутся из словаря по ключу scope.<value>. */
const PRICE_SCOPES: readonly PriceScope[] = ['one_way', 'round_trip_half', 'round_trip_full'];



function isWarn(missing: string[], field: string): boolean {
  return missing.includes(field);
}

interface FieldProps {
  id: string;
  label: string;
  warn?: boolean;
  error?: string;
  children: ReactNode;
}

function Field({ id, label, warn, error, children }: FieldProps) {
  return (
    <div className={`${styles.field} ${warn ? styles.fieldWarn : ''}`}>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      {children}
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

export interface DraftCardProps {
  index: number;
  form: DraftForm;
  errors: DraftFormErrors;
  onChange(patch: Partial<DraftForm>): void;
}

export function DraftCard({ index, form, errors, onChange }: DraftCardProps) {
  const { currencies } = useStore();
  const t = useT();
  const missingLabels = form.missing.map((m) => t(`parse.missing.${m}`));
  const fromTzListId = `draft-${index}-from-tz-list`;
  const toTzListId = `draft-${index}-to-tz-list`;
  const ids = {
    include: `draft-${index}-include`,
    from: `draft-${index}-from`,
    to: `draft-${index}-to`,
    fromTz: `draft-${index}-from-tz`,
    toTz: `draft-${index}-to-tz`,
    depart: `draft-${index}-depart`,
    arrive: `draft-${index}-arrive`,
    carrier: `draft-${index}-carrier`,
    flightNumber: `draft-${index}-flight-number`,
    price: `draft-${index}-price`,
    currency: `draft-${index}-currency`,
    priceScope: `draft-${index}-price-scope`,
  };

  return (
    <fieldset className={styles.card}>
      <legend className={styles.cardLegend}>
        <label className={styles.includeLabel} htmlFor={ids.include}>
          <input
            id={ids.include}
            type="checkbox"
            checked={form.include}
            onChange={(e) => onChange({ include: e.target.checked })}
          />
          <span>
            {t('parse.flightN', { n: index + 1 })}
            {form.from && form.to ? (
              <>
                : <span className="iata">{form.from}</span> → <span className="iata">{form.to}</span>
              </>
            ) : null}
          </span>
        </label>
      </legend>

      {missingLabels.length > 0 && (
        <p className={styles.missingBanner}>
          {t('parse.notRecognised', { fields: missingLabels.join(', ') })}
        </p>
      )}
      {errors.tzWarning && <p className={styles.missingBanner}>{errors.tzWarning}</p>}

      <div className={styles.row}>
        <Field id={ids.from} label={t('field.fromIata')} warn={isWarn(form.missing, 'from')} error={errors.from}>
          <input
            id={ids.from}
            className="input mono"
            value={form.from}
            maxLength={3}
            aria-invalid={errors.from ? true : undefined}
            onChange={(e) => onChange({ from: e.target.value.toUpperCase() })}
          />
        </Field>
        <Field id={ids.to} label={t('field.toIata')} warn={isWarn(form.missing, 'to')} error={errors.to}>
          <input
            id={ids.to}
            className="input mono"
            value={form.to}
            maxLength={3}
            aria-invalid={errors.to ? true : undefined}
            onChange={(e) => onChange({ to: e.target.value.toUpperCase() })}
          />
        </Field>
      </div>

      <div className={styles.row}>
        <Field id={ids.fromTz} label={t('field.tzFrom')} warn={isWarn(form.missing, 'fromTz')}>
          <input
            id={ids.fromTz}
            className="input"
            value={form.fromTz}
            list={fromTzListId}
            placeholder="Europe/Nicosia"
            onChange={(e) => onChange({ fromTz: e.target.value })}
          />
          <datalist id={fromTzListId}>
            {KNOWN_TIMEZONES.map((tz) => (
              <option key={tz} value={tz} />
            ))}
          </datalist>
        </Field>
        <Field id={ids.toTz} label={t('field.tzTo')} warn={isWarn(form.missing, 'toTz')}>
          <input
            id={ids.toTz}
            className="input"
            value={form.toTz}
            list={toTzListId}
            placeholder="Asia/Yerevan"
            onChange={(e) => onChange({ toTz: e.target.value })}
          />
          <datalist id={toTzListId}>
            {KNOWN_TIMEZONES.map((tz) => (
              <option key={tz} value={tz} />
            ))}
          </datalist>
        </Field>
      </div>

      <div className={styles.row}>
        <Field
          id={ids.depart}
          label={t('field.departLocal')}
          warn={isWarn(form.missing, 'departLocal') || isWarn(form.missing, 'departTime')}
          error={errors.departLocal}
        >
          <input
            id={ids.depart}
            className="input"
            type="datetime-local"
            value={form.departLocal}
            aria-invalid={errors.departLocal ? true : undefined}
            onChange={(e) => onChange({ departLocal: e.target.value })}
          />
        </Field>
        <Field
          id={ids.arrive}
          label={t('field.arriveLocal')}
          warn={isWarn(form.missing, 'arriveLocal') || isWarn(form.missing, 'arriveTime')}
          error={errors.arriveLocal}
        >
          <input
            id={ids.arrive}
            className="input"
            type="datetime-local"
            value={form.arriveLocal}
            aria-invalid={errors.arriveLocal ? true : undefined}
            onChange={(e) => onChange({ arriveLocal: e.target.value })}
          />
        </Field>
      </div>

      <div className={styles.row}>
        <Field id={ids.carrier} label={t('field.carrier')} warn={isWarn(form.missing, 'carrier')}>
          <input
            id={ids.carrier}
            className="input"
            value={form.carrier}
            onChange={(e) => onChange({ carrier: e.target.value })}
          />
        </Field>
        <Field id={ids.flightNumber} label={t('field.flightNumber')}>
          <input
            id={ids.flightNumber}
            className="input"
            value={form.flightNumber}
            placeholder={t('form.optional')}
            onChange={(e) => onChange({ flightNumber: e.target.value })}
          />
        </Field>
      </div>

      <div className={styles.row}>
        <Field id={ids.price} label={t('field.price')} warn={isWarn(form.missing, 'price')} error={errors.amount}>
          <div className={styles.moneyRow}>
            <input
              id={ids.price}
              className="input mono"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={form.amount}
              aria-invalid={errors.amount ? true : undefined}
              onChange={(e) => onChange({ amount: e.target.value })}
            />
            <select
              id={ids.currency}
              className="select"
              aria-label={t('toolbar.currency')}
              value={form.currency}
              onChange={(e) => onChange({ currency: e.target.value as Currency })}
            >
              {currencies.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field id={ids.priceScope} label={t('field.priceScope')}>
          <select
            id={ids.priceScope}
            className="select"
            value={form.priceScope}
            onChange={(e) => onChange({ priceScope: e.target.value as PriceScope })}
          >
            {PRICE_SCOPES.map((scope) => (
              <option key={scope} value={scope}>
                {t(`scope.${scope}`)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {form.notes.length > 0 && (
        <ul className={styles.notes}>
          {form.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}
